import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { fitFor, FIT_META } from '../../lib/collegeFit';
import '../../college-theme.css';

/* ─── Constants ──────────────────────────────────────────────────────── */

const NUMBER_FIELDS = [
  { key: 'satComposite', label: 'SAT composite', type: 'number', min: 400, max: 1600, step: 10 },
  { key: 'ibPredicted', label: 'IB predicted / 45', type: 'number', min: 0, max: 45, step: 1 },
  { key: 'gpa', label: 'GPA', type: 'number', min: 0, max: 4, step: 0.01 },
  { key: 'gradYear', label: 'Grad year', type: 'number' },
  { key: 'intendedMajor', label: 'Intended major', type: 'text', small: true },
];

const emptyForm = () => ({
  satComposite: '', ibPredicted: '', gpa: '', gradYear: '', intendedMajor: '',
});

function toFormValue(v) {
  return v == null ? '' : String(v);
}

function toPayload(form) {
  return {
    satComposite: form.satComposite === '' ? null : Number(form.satComposite),
    ibPredicted: form.ibPredicted === '' ? null : Number(form.ibPredicted),
    gpa: form.gpa === '' ? null : Number(form.gpa),
    gradYear: form.gradYear === '' ? null : Number(form.gradYear),
    intendedMajor: form.intendedMajor.trim() === '' ? null : form.intendedMajor.trim(),
  };
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function CollegeProfilePage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [uploadingResume, setUploadingResume] = useState(false);
  const [removingResume, setRemovingResume] = useState(false);

  const formRef = useRef(form);
  formRef.current = form;
  const debounceTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get('/student-profile'), api.get('/college-applications')])
      .then(([profileRes, appsRes]) => {
        if (!alive) return;
        const p = profileRes.data.profile || null;
        setProfile(p);
        setForm({
          satComposite: toFormValue(p?.satComposite),
          ibPredicted: toFormValue(p?.ibPredicted),
          gpa: toFormValue(p?.gpa),
          gradYear: toFormValue(p?.gradYear),
          intendedMajor: p?.intendedMajor || '',
        });
        setApplications(appsRes.data.applications || []);
      })
      .catch(() => {
        if (alive) toast.error('Failed to load your profile');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); }, []);

  const persistProfile = async () => {
    const payload = toPayload(formRef.current);
    try {
      const res = await api.put('/student-profile', payload);
      setProfile(res.data.profile);
      toast.success('Saved', { duration: 1500 });
      // Re-fetch so fit/fitColor/fitBg on each application reflect the
      // numbers we just saved (the verdict chips below also live-update
      // instantly via fitFor(), but this keeps the two in sync).
      try {
        const appsRes = await api.get('/college-applications');
        setApplications(appsRes.data.applications || []);
      } catch {
        // non-fatal — chips will just rely on the live client-side computation
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save your numbers');
    }
  };

  const handleFieldChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => { persistProfile(); }, 500);
  };

  /* ── resume ── */

  const handleResumeFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    setUploadingResume(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(file);
      });
      const commaIdx = dataUrl.indexOf(',');
      const dataBase64 = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
      const res = await api.post('/student-profile/resume', { filename: file.name, dataBase64 });
      setProfile(res.data.profile);
      toast.success('Resume parsed — Gemini will weigh it in every fit read');
    } catch (err) {
      if (err.response?.status === 422) {
        toast.error(err.response?.data?.error || "Couldn't extract text from that PDF");
      } else if (err.response?.status === 503) {
        toast.error(err.response?.data?.error || 'Resume parsing is not configured right now');
      } else {
        toast.error(err.response?.data?.error || 'Failed to upload resume');
      }
    } finally {
      setUploadingResume(false);
    }
  };

  const handleRemoveResume = async () => {
    setRemovingResume(true);
    try {
      const res = await api.delete('/student-profile/resume');
      setProfile(res.data.profile);
      toast.success('Resume removed');
    } catch {
      toast.error('Failed to remove resume');
    } finally {
      setRemovingResume(false);
    }
  };

  /* ── live verdicts ── */

  const liveProfile = useMemo(() => ({
    satComposite: form.satComposite === '' ? null : Number(form.satComposite),
    ibPredicted: form.ibPredicted === '' ? null : Number(form.ibPredicted),
  }), [form.satComposite, form.ibPredicted]);

  const verdictChips = useMemo(() => applications.map((app) => {
    const liveFit = fitFor(liveProfile, app);
    const fit = liveFit || app.fit || null;
    const meta = liveFit ? FIT_META[liveFit] : null;
    const fitColor = meta ? meta.color : app.fitColor;
    const fitBg = meta ? meta.bg : app.fitBg;
    return { app, fit, fitColor, fitBg };
  }), [applications, liveProfile]);

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>Loading your numbers…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }} className="animate-fade-up">
      <style>{`
        @media (max-width: 900px) {
          .college-profile-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 520px) {
          .college-profile-grid { grid-template-columns: 1fr !important; }
          .college-profile-grid > div[data-span2] { grid-column: auto !important; }
          .college-profile-resume { flex-direction: column !important; align-items: flex-start !important; }
          .college-profile-resume > div:last-child { margin-top: 16px; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <h1 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 52, lineHeight: 1, margin: '0 0 12px' }}>
          Your numbers.
        </h1>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--college-muted)', maxWidth: '56ch', lineHeight: 1.55 }}>
          Everything on this desk compares schools against these figures. Change one and the fit verdicts and
          Gemini reads recompute.
        </p>
      </div>

      {/* Numbers grid */}
      <div
        className="college-profile-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 28, marginBottom: 48 }}
      >
        {NUMBER_FIELDS.map((field) => (
          <div
            key={field.key}
            data-span2={field.key === 'intendedMajor' ? true : undefined}
            style={{
              borderTop: '2px solid var(--college-ink)',
              paddingTop: 14,
              gridColumn: field.key === 'intendedMajor' ? 'span 2' : undefined,
            }}
          >
            <p className="label-editorial" style={{ margin: '0 0 10px' }}>{field.label}</p>
            <input
              type={field.type}
              inputMode={field.type === 'number' ? 'decimal' : undefined}
              min={field.min}
              max={field.max}
              step={field.step}
              value={form[field.key]}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder="—"
              className="font-serif-editorial"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid var(--college-ink)',
                background: 'var(--college-panel)',
                color: 'var(--college-ink)',
                padding: 12,
                fontSize: field.small ? 22 : 30,
                outline: 'none',
              }}
            />
          </div>
        ))}
      </div>

      {/* Resume */}
      <div
        className="college-profile-resume"
        style={{
          border: '1px solid var(--college-ink)',
          background: 'var(--college-panel)',
          padding: '22px 26px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          marginBottom: 56,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="label-editorial" style={{ margin: '0 0 10px' }}>Resume — feeds Gemini fit reads</p>
          {profile?.resumeFileName ? (
            <>
              <p style={{ margin: 0, fontSize: 15, color: 'var(--college-ink)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="font-mono-editorial">{profile.resumeFileName}</span>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3E6B44' }}>
                  parsed
                </span>
                <button
                  type="button"
                  onClick={handleRemoveResume}
                  disabled={removingResume}
                  style={{
                    background: 'none', border: 'none', padding: 0, fontSize: 13, color: 'var(--college-red)',
                    textDecoration: 'underline', cursor: removingResume ? 'default' : 'pointer', opacity: removingResume ? 0.6 : 1,
                  }}
                >
                  {removingResume ? 'removing…' : 'remove'}
                </button>
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--college-muted)', maxWidth: '52ch' }}>
                Gemini now weighs your activities and awards alongside your numbers in every fit read.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--college-ink-soft)', maxWidth: '52ch', lineHeight: 1.5 }}>
              Upload your resume as a PDF. Gemini reads your activities, leadership and awards to sharpen the
              fit verdicts beyond raw numbers.
            </p>
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid var(--college-ink)',
              background: uploadingResume ? 'var(--college-border-soft)' : 'var(--college-ink)',
              color: uploadingResume ? 'var(--college-ink-soft)' : 'var(--college-panel)',
              padding: '10px 20px',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: uploadingResume ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {uploadingResume ? 'Parsing…' : (profile?.resumeFileName ? 'Replace PDF' : 'Upload PDF')}
            <input
              type="file"
              accept="application/pdf"
              onChange={handleResumeFile}
              disabled={uploadingResume}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {/* Resulting verdicts */}
      <div style={{ borderBottom: '2px solid var(--college-ink)', paddingBottom: 10, marginBottom: 20 }}>
        <p className="label-editorial" style={{ margin: 0 }}>Resulting verdicts</p>
      </div>

      {verdictChips.length === 0 ? (
        <p style={{ fontSize: 14, color: 'var(--college-muted)' }}>
          No schools on your list yet — add some from{' '}
          <span
            onClick={() => navigate('/college/list')}
            style={{ color: 'var(--college-ink)', textDecoration: 'underline', cursor: 'pointer' }}
          >
            My List
          </span>{' '}
          to see verdicts here.
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {verdictChips.map(({ app, fit, fitColor, fitBg }) => (
            <div
              key={app.id}
              onClick={() => navigate(`/college/${app.id}`)}
              title={app.collegeName}
              style={{
                border: '1px solid var(--college-border)',
                background: 'var(--college-panel)',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
              }}
            >
              <span
                className="font-serif-editorial"
                style={{
                  fontSize: 17,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 200,
                }}
              >
                {app.collegeName}
              </span>
              {fit ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    padding: '4px 12px',
                    borderRadius: 999,
                    background: fitBg,
                    color: fitColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fit}
                </span>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--college-muted-2)' }}>no data yet</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
